import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { mergeMap, of, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { FollowupService } from '~/repositories/followup/services/followup.service';
import * as FollowupActions from '~/repositories/followup/store/followup.actions';
import { IState } from '~/repositories/repositories.store';

@Injectable()
export class FollowupEffects {
  fetchFollowupsForDecree$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowupActions.fetchFollowupsForDecree),
      mergeMap(({ decreeId }) => {
        return this.followupService
          .fetchFollowupTasksForDecree(decreeId)
          .pipe(map((followupTasks) => FollowupActions.fetchFollowupsForDecreeSuccess({ followupTasks })));
      }),
      catchError((error) => of(FollowupActions.fetchFollowupsForDecreeFailure({ error })))
    )
  );

  publishFollowupTask = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowupActions.publishFollowup),
      mergeMap(({ followup, objectMetaId }) => {
        return this.followupService.publishFollowupTask(followup, objectMetaId).pipe(
          map((followup) =>
            FollowupActions.publishFollowupSuccess({
              followup
            })
          ),
          tap(({ followup }) => {
            this.messageService.add({
              severity: 'success',
              detail: this.translateService.instant('layout.followup.message.publish.success'),
              life: 3000
            });
          }),
          catchError((error) => of(FollowupActions.publishFollowupFailure({ error })))
        );
      })
    )
  );

  removeFollowupTask = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowupActions.removeFollowupTask),
      mergeMap(({ taskId }) => {
        return this.followupService.deleteFollowupTask(taskId).pipe(
          map(() =>
            FollowupActions.removeFollowupTaskSuccess({
              taskId
            })
          ),
          tap(() => {
            this.messageService.add({
              severity: 'success',
              detail: this.translateService.instant('layout.followup.message.remove.success'),
              life: 3000
            });
          }),
          catchError((error) => of(FollowupActions.removeFollowupTaskFailed({ error })))
        );
      })
    )
  );

  updateFollowupTask = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowupActions.updateFollowupTask),
      mergeMap(({ taskId, followupTask }) => {
        return this.followupService.updateFollowupTask(taskId, followupTask).pipe(
          map((followupTask) =>
            FollowupActions.updateFollowupTaskSuccess({
              followupTask
            })
          ),
          tap(() => {
            this.messageService.add({
              severity: 'success',
              detail: this.translateService.instant('layout.followup.message.update.task.success'),
              life: 3000
            });
          }),
          catchError((error) => of(FollowupActions.updateFollowupTaskFailed({ error })))
        );
      })
    )
  );

  updateFollowupStatus = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowupActions.updateStatus),
      mergeMap(({ taskIds, status, additionalInfo }) => {
        return this.followupService.updateFollowupStatus(taskIds, status, additionalInfo).pipe(
          map((followupTasks) =>
            FollowupActions.updateStatusSuccess({
              followupTasks
            })
          ),
          tap(() => {
            this.messageService.add({
              severity: 'success',
              detail: this.translateService.instant('layout.followup.message.update.status.success'),
              life: 3000
            });
          }),
          catchError((error) => of(FollowupActions.updateStatusFailed({ error })))
        );
      })
    )
  );

  assignTasks = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowupActions.assignTasks),
      mergeMap(({ taskIds, assignedUserId }) => {
        return this.followupService.assignTasks(taskIds, assignedUserId).pipe(
          map((followupTasks) =>
            FollowupActions.assignTasksSuccess({
              followupTasks
            })
          ),
          tap(() => {
            this.messageService.add({
              severity: 'success',
              detail: this.translateService.instant('layout.followup.message.assign.task.success'),
              life: 3000
            });
          }),
          catchError((error) => of(FollowupActions.assignTasksFailed({ error })))
        );
      })
    )
  );

  // collection of all failures that have to show a toast message
  failures$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(...[FollowupActions.fetchFollowupsForDecreeFailure]),
        tap(({ error }) => {
          if (error.status == 401) {
            this.messageService.add({
              severity: 'error',
              detail: 'unauthorized!',
              life: 3000
            });
            this.router.navigate(['/error'], { replaceUrl: true }).then(() => true);
          } else {
            this.messageService.add({
              severity: 'error',
              detail: this.translateService.instant('layout.holiday.message.failed'),
              life: 3000
            });
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private store$: Store<IState>,
    private readonly followupService: FollowupService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly router: Router
  ) {}
}
